import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";

const { RangePicker } = DatePicker;

export type RangeValue = [Dayjs | null, Dayjs | null];

interface CustomRangePickerProps {
    onChange: (dates: RangeValue) => void;
    value?: RangeValue;
    allowClear?: boolean;
    style?: React.CSSProperties;
    placeholder?: [string, string];
}

const getToday = (): [Dayjs, Dayjs] => {
    const now = dayjs();
    return [now.startOf("day"), now.endOf("day")];
};

const getLast7Days = (): [Dayjs, Dayjs] => {
    const now = dayjs();
    return [now.subtract(6, "day").startOf("day"), now.endOf("day")];
};

const getLast15Days = (): [Dayjs, Dayjs] => {
    const now = dayjs();
    return [now.subtract(14, "day").startOf("day"), now.endOf("day")];
};

const getThisMonth = (): [Dayjs, Dayjs] => {
    const now = dayjs();
    return [now.startOf("month"), now.endOf("day")];
};

const getLastMonth = (): [Dayjs, Dayjs] => {
    const now = dayjs().subtract(1, "month");
    return [now.startOf("month"), now.endOf("month")];
};

const getThisYear = (): [Dayjs, Dayjs] => {
    const now = dayjs();
    return [now.startOf("year"), now.endOf("day")];
};

const getLastYear = (): [Dayjs, Dayjs] => {
    const now = dayjs().subtract(1, "year");
    return [now.startOf("year"), now.endOf("year")];
};

const isSameRange = (
    range1: RangeValue,
    range2: [Dayjs, Dayjs]
) => {
    if (!range1[0] || !range1[1]) return false;

    return (
        range1[0].isSame(range2[0], "day") &&
        range1[1].isSame(range2[1], "day")
    );
};

const getActivePreset = (value?: RangeValue) => {
    if (!value) return undefined;

    if (isSameRange(value, getToday())) return "Today";
    if (isSameRange(value, getLast7Days())) return "Last 7 Days";
    if (isSameRange(value, getLast15Days())) return "Last 15 Days";
    if (isSameRange(value, getThisMonth())) return "This Month";
    if (isSameRange(value, getLastMonth())) return "Last Month";
    if (isSameRange(value, getThisYear())) return "This Year";
    if (isSameRange(value, getLastYear())) return "Last Year";

    return undefined;
};


const CustomRangePicker: React.FC<CustomRangePickerProps> = ({
    onChange,
    value,
    allowClear = true,
    style,
    placeholder = ["From Date", "To Date"],
}) => {
    const activePreset = getActivePreset(value);
    return (
        <RangePicker
            allowClear={allowClear}
            placeholder={placeholder}
            style={{ width: "100%", ...style }}
            format="DD/MM/YYYY"
            value={value}
            onChange={(dates) =>
                onChange([dates?.[0] || null, dates?.[1] || null])
            }
            presets={[
                { label: activePreset === "Today" ? <b>Today</b> : "Today", value: getToday() },
                { label: activePreset === "Last 7 Days" ? <b>Last 7 Days</b> : "Last 7 Days", value: getLast7Days() },
                { label: activePreset === "Last 15 Days" ? <b>Last 15 Days</b> : "Last 15 Days", value: getLast15Days() },
                { label: activePreset === "This Month" ? <b>This Month</b> : "This Month", value: getThisMonth() },
                { label: activePreset === "Last Month" ? <b>Last Month</b> : "Last Month", value: getLastMonth() },
                { label: activePreset === "This Year" ? <b>This Year</b> : "This Year", value: getThisYear() },
                { label: activePreset === "Last Year" ? <b>Last Year</b> : "Last Year", value: getLastYear() },
            ]}
        />
    );
};

export default CustomRangePicker;